<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { FormKit } from '@formkit/vue';
import { useBranding } from 'shared/composables/useBranding';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    FormKit,
    NextButton,
  },
  props: {
    integrationId: {
      type: String,
      required: true,
    },
    hook: {
      type: Object,
      default: null,
    },
  },
  emits: ['close'],
  setup(props) {
    const { integration, isHookTypeInbox } = useIntegrationHook(
      props.integrationId
    );
    const { replaceInstallationName } = useBranding();

    return { integration, isHookTypeInbox, replaceInstallationName };
  },
  data() {
    return {
      endPoint: '',
      alertMessage: '',
      values: {},
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'integrations/getUIFlags',
      dialogFlowEnabledInboxes: 'inboxes/dialogFlowEnabledInboxes',
    }),
    inboxes() {
      return this.dialogFlowEnabledInboxes
        .filter(inbox => {
          if (!this.isIntegrationDialogflow) {
            return true;
          }
          return (
            this.selectedInboxIds.includes(Number(inbox.id)) ||
            !this.connectedDialogflowInboxIds.includes(inbox.id)
          );
        })
        .map(inbox => ({ label: inbox.name, value: inbox.id }));
    },

    connectedDialogflowInboxIds() {
      if (!this.isIntegrationDialogflow) {
        return [];
      }
      return this.integration.hooks.map(hook => hook.inbox?.id);
    },
    formItems() {
      const schema = this.integration.settings_form_schema || [];
      if (!this.isEditing) {
        return schema;
      }
      return schema.filter(item => item.name !== 'api_key');
    },
    isEditing() {
      return !!this.hook?.id;
    },
    isIntegrationDialogflow() {
      return this.integration.id === 'dialogflow';
    },
    submitButtonLabel() {
      if (
        this.integration.id === 'openai' &&
        (this.uiFlags.isCreatingHook || this.uiFlags.isUpdatingHook)
      ) {
        return this.$t('INTEGRATION_APPS.ADD.FORM.VALIDATING_OPENAI');
      }

      return this.isEditing
        ? this.$t('INTEGRATION_APPS.EDIT.FORM.SUBMIT')
        : this.$t('INTEGRATION_APPS.ADD.FORM.SUBMIT');
    },
    isSubmitting() {
      return this.uiFlags.isCreatingHook || this.uiFlags.isUpdatingHook;
    },
    selectedInboxIds() {
      const inboxes = this.values.inboxes || this.values.inbox || [];
      return (Array.isArray(inboxes) ? inboxes : [inboxes])
        .map(inboxId => Number(inboxId))
        .filter(Boolean);
    },
  },
  watch: {
    hook: {
      immediate: true,
      handler() {
        this.prefillForm();
      },
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    prefillForm() {
      if (!this.hook) {
        this.values = {};
        return;
      }

      const values = { ...(this.hook.settings || {}) };
      (this.formItems || []).forEach(item => {
        if (item.validation?.includes('JSON') && values[item.name]) {
          values[item.name] = JSON.stringify(values[item.name], null, 2);
        }
      });

      if (this.isHookTypeInbox && this.hook.inbox?.id) {
        values.inboxes = [this.hook.inbox.id];
      }

      this.values = values;
    },
    buildHookPayload(inboxId) {
      const hookPayload = {
        app_id: this.integration.id,
        settings: { ...(this.hook?.settings || {}) },
      };

      hookPayload.settings = Object.keys(this.values).reduce((acc, key) => {
        if (!['inbox', 'inboxes'].includes(key)) {
          acc[key] = this.values[key];
        }
        return acc;
      }, hookPayload.settings);

      this.formItems.forEach(item => {
        if (item.validation?.includes('JSON')) {
          hookPayload.settings[item.name] = JSON.parse(
            hookPayload.settings[item.name]
          );
        }
      });

      if (this.isHookTypeInbox && inboxId) {
        hookPayload.inbox_id = inboxId;
      }

      return hookPayload;
    },
    findHookForInbox(inboxId) {
      return this.integration.hooks.find(
        hook => Number(hook.inbox?.id) === Number(inboxId)
      );
    },
    async upsertInboxHooks() {
      const selectedInboxIds = this.selectedInboxIds;
      if (!selectedInboxIds.length) {
        throw new Error(this.$t('INTEGRATION_APPS.ADD.FORM.INBOX.REQUIRED'));
      }

      await Promise.all(
        selectedInboxIds.map(inboxId => {
          const existingHook = this.findHookForInbox(inboxId);
          const hookData = this.buildHookPayload(inboxId);

          if (existingHook) {
            return this.$store.dispatch('integrations/updateHook', {
              hookId: existingHook.id,
              hookData,
            });
          }

          return this.$store.dispatch('integrations/createHook', hookData);
        })
      );
    },
    async submitForm() {
      try {
        if (this.isHookTypeInbox) {
          await this.upsertInboxHooks();
          this.alertMessage = this.$t(
            this.isEditing
              ? 'INTEGRATION_APPS.EDIT.API.SUCCESS_MESSAGE'
              : 'INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE'
          );
        } else if (this.isEditing) {
          await this.$store.dispatch('integrations/updateHook', {
            hookId: this.hook.id,
            hookData: this.buildHookPayload(),
          });
          this.alertMessage = this.$t(
            'INTEGRATION_APPS.EDIT.API.SUCCESS_MESSAGE'
          );
        } else {
          await this.$store.dispatch(
            'integrations/createHook',
            this.buildHookPayload()
          );
          this.alertMessage = this.$t(
            'INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE'
          );
        }
        this.onClose();
      } catch (error) {
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage ||
          error?.message ||
          this.$t(
            this.isEditing
              ? 'INTEGRATION_APPS.EDIT.API.ERROR_MESSAGE'
              : 'INTEGRATION_APPS.ADD.API.ERROR_MESSAGE'
          );
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto integration-hooks">
    <woot-modal-header
      :header-title="integration.name"
      :header-content="replaceInstallationName(integration.short_description)"
    />
    <FormKit
      v-model="values"
      type="form"
      form-class="w-full grid gap-4"
      :submit-attrs="{
        inputClass: 'hidden',
        wrapperClass: 'hidden',
      }"
      :incomplete-message="false"
      @submit="submitForm"
    >
      <FormKit v-for="item in formItems" :key="item.name" v-bind="item" />
      <FormKit
        v-if="isHookTypeInbox"
        :options="inboxes"
        type="checkbox"
        name="inboxes"
        :label="$t('INTEGRATION_APPS.ADD.FORM.INBOX.MULTIPLE_LABEL')"
        :help="$t('INTEGRATION_APPS.ADD.FORM.INBOX.MULTIPLE_HELP')"
        validation="required"
        :validation-name="$t('INTEGRATION_APPS.ADD.FORM.INBOX.MULTIPLE_LABEL')"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="submitButtonLabel"
          :is-loading="isSubmitting"
        />
      </div>
    </FormKit>
  </div>
</template>

<style lang="css">
.formkit-outer {
  @apply mt-2;
}

.formkit-form > .formkit-wrapper > ul.formkit-messages {
  @apply hidden;
}

.formkit-form .formkit-help {
  @apply text-n-slate-10 text-sm font-normal mt-2 w-full;
}

/* equivalent of .reset-base */
.formkit-input {
  margin-bottom: 0px !important;
}

[data-invalid] .formkit-message {
  @apply text-n-ruby-9 block text-xs font-normal my-1 w-full;
}

.formkit-outer[data-type='checkbox'] .formkit-wrapper {
  @apply flex items-center gap-2 px-0.5;
}

.formkit-messages {
  @apply list-none m-0 p-0;
}

.formkit-actions {
  @apply hidden;
}
</style>
